import { ArrowUp } from 'lucide-react';
import { ChangeEvent, FormEvent, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

export const MessageInput = ({
  onSendMessage,
  feedback,
  emitTypingStatus,
}: {
  conversationId: string;
  nameRef: React.MutableRefObject<string>;
  useridRef: React.MutableRefObject<string>;
  imageRef: React.MutableRefObject<string>;
  onSendMessage: (message: string) => void;
  emitTypingStatus: (isUserTyping: boolean) => void;
  feedback: string;
}) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [textareaHeight, setTextareaHeight] = useState('');

  const handleSendMessage = (e: FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
      emitTypingStatus(false);
      const textarea = inputRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
      }
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const length = e.target.value.length;
    emitTypingStatus(length > 0);
    setMessage(e.target.value);
    adjustInputHeight();
  };

  const adjustInputHeight = () => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = textarea.scrollHeight;
      textarea.style.height = `${newHeight}px`;
      if (newHeight < 144) {
        setTextareaHeight(`${newHeight}px`);
      }
      textarea.scrollTop = newHeight;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <footer className="border-t-stroke sticky bottom-0 left-0 right-0 border-t">
      <label className="sr-only" htmlFor="chat-box">
        Message
      </label>
      <form className="pb-2 ps-2" onSubmit={handleSendMessage}>
        <textarea
          maxLength={1000}
          ref={inputRef}
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          style={{ height: textareaHeight }}
          id="chat-box"
          placeholder="Send a message"
          className="block max-h-36 min-h-20 w-full resize-none border-transparent bg-transparent pb-2 pe-4 ps-2 pt-4 text-sm text-white outline-hidden"
        />
        <div className="flex items-center justify-end gap-x-1 pe-2">
          <Button size="icon" className="rounded-full" type="submit">
            <ArrowUp />
          </Button>
        </div>
      </form>
      <div className="ml-5 h-5 text-xs">{feedback}</div>
    </footer>
  );
};
